import { addHours, dateOf, isValidLocal, toInstant, toLocal, type LocalDate } from './time';
import { assertNonNegative, inr, sum, type Rupees } from './money';
import { DomainError, fail } from './errors';
import { newId } from './ids';
import { advanceFor, calc, cleaningFor, depositFor, quoteItem, requirePrice, span, termsFrom } from './pricing';
import { conflicts, phase, type Conflict } from './availability';
import type {
  BookingRecord, Block, Comm, Ctx, Customer, Deduction, DocRef, Item, Payment, PaymentType, Phase, RecordKind, World,
} from './types';

/**
 * Record commands. Every function is pure: it takes a record and returns a NEW record (the input is
 * never mutated), validates the transition, and appends an audit log entry. Persistence and sync wrap
 * these; they never edit records directly.
 */

const nowLocal = (ctx: Ctx) => toLocal(ctx.now);
const today = (ctx: Ctx): LocalDate => dateOf(ctx.now);

function clone<T>(x: T): T { return structuredClone(x); }
function log(r: BookingRecord, ctx: Ctx, t: string) { r.log.push({ d: nowLocal(ctx), t, by: ctx.actor }); }

function requirePhase(r: BookingRecord, ctx: Ctx, allowed: Phase[], action: string): Phase {
  const ph = phase(r, ctx.now);
  if (!allowed.includes(ph)) fail('wrong-state', `Cannot ${action} a record that is ${ph}`);
  return ph;
}

function requireAvailable(world: World, r: BookingRecord, ctx: Ctx): void {
  const sp = span(r);
  const cs = conflicts(world, sp.start, sp.end, { now: ctx.now, bufferHours: r.terms.bufferHours, excludeId: r.id });
  if (cs.length) throw new DomainError('unavailable', 'These dates clash with another hold, booking or block', cs);
}

/** Expired holds that are still stored as "hold" become "expired", with history kept. Idempotent. */
export function normalizeExpiry(r: BookingRecord, now: number): BookingRecord {
  if (r.status !== 'hold' || !r.hold || toInstant(r.hold.expires) > now) return r;
  const n = clone(r);
  n.status = 'expired';
  n.log.push({ d: r.hold!.expires, t: 'Hold expired — dates released', by: 'system' });
  return n;
}

/* ---------------- creation ---------------- */

export interface ItemInput { pkg: string; start: string; end: string; agreed?: Rupees }

export interface NewRecordInput {
  id?: string;
  cust: string;
  kind: RecordKind;
  type: string;
  items: ItemInput[];
  guests: number;
  cleaning?: Rupees;
  extra?: BookingRecord['extra'];
  discount?: BookingRecord['discount'];
  source: string;
  followUp?: LocalDate | null;
  notes?: string;
  status: 'enquiry' | 'hold' | 'confirmed';
  holdHours?: number;
  advance?: { id?: string; amount: Rupees; mode: string; ref?: string; date?: LocalDate } | null;
}

export interface Result<T> { value: T; warnings: string[] }

function buildItems(input: NewRecordInput, ctx: Ctx): Item[] {
  if (!input.items.length) fail('invalid', 'Choose a package and dates');
  const kinds = new Set<string>();
  const items = input.items.map(i => {
    const pkg = ctx.settings.packages.find(p => p.id === i.pkg) ?? fail('invalid', `Unknown package ${i.pkg}`);
    if (!isValidLocal(i.start) || !isValidLocal(i.end)) fail('invalid', 'Enter valid dates and times');
    kinds.add(pkg.kind);
    return quoteItem(pkg, i.start, i.end, i.agreed);
  });
  const expect = input.kind === 'combo' ? ['event', 'stay'] : [input.kind];
  if (kinds.size !== expect.length || !expect.every(k => kinds.has(k))) fail('invalid', `A ${input.kind} booking needs ${expect.join(' + ')} items`);
  return items;
}

export function createRecord(input: NewRecordInput, world: World, ctx: Ctx): Result<BookingRecord> {
  const warnings: string[] = [];
  if (!input.cust) fail('invalid', 'Choose or add a customer');
  if (!Number.isInteger(input.guests) || input.guests <= 0) fail('invalid', 'Enter the number of guests');
  const items = buildItems(input, ctx);
  const cleaning = input.cleaning ?? cleaningFor(items, ctx.settings);
  assertNonNegative(cleaning, 'cleaning');
  if (input.extra) assertNonNegative(input.extra.amount, 'extra-guest charge');
  if (input.discount) assertNonNegative(input.discount.amount, 'discount');
  const created = nowLocal(ctx);
  const r: BookingRecord = {
    id: input.id ?? newId(ctx.now), number: null, cust: input.cust, kind: input.kind, type: input.type, items,
    guests: input.guests, cleaning, extra: input.extra ?? null, discount: input.discount ?? null, damage: 0,
    terms: termsFrom(ctx.settings), status: 'enquiry', hold: null, holdHistory: [], source: input.source, created,
    followUp: input.followUp ?? null, notes: input.notes ?? '', payments: [],
    deposit: { required: depositFor(items), status: 'due', collected: null, deductions: [], refund: null, notes: '', evidence: [] },
    docs: [], comms: [], log: [{ d: created, t: `Enquiry received via ${input.source}`, by: ctx.actor }],
    checkout: null, cancel: null, lost: null, completedAt: null,
  };
  const k = calc(r);
  if (k.total <= 0 && !k.pending) fail('invalid', 'Total must be more than ₹0');
  for (const it of items) {
    const pkg = ctx.settings.packages.find(p => p.id === it.pkg)!;
    if (input.guests > (pkg.max ?? pkg.capacity)) warnings.push(`${input.guests} guests is above the maximum of ${pkg.max ?? pkg.capacity} for ${pkg.name}`);
    else if (input.guests > pkg.capacity) warnings.push(`${input.guests} guests is above the standard capacity of ${pkg.capacity} for ${pkg.name}`);
  }
  if (input.status === 'enquiry') return { value: r, warnings };
  const held = placeHold(r, input.holdHours ?? ctx.settings.holdHours, world, ctx).value;
  if (input.status === 'hold') return { value: held, warnings };
  const conf = confirm(held, { advance: input.advance ?? null }, world, ctx);
  return { value: conf.value, warnings: [...warnings, ...conf.warnings] };
}

/* ---------------- holds ---------------- */

export function placeHold(r: BookingRecord, hours: number, world: World, ctx: Ctx): Result<BookingRecord> {
  requirePhase(r, ctx, ['enquiry', 'expired'], 'place a hold on');
  requirePrice(r, 'placing a hold');
  if (!(hours > 0)) fail('invalid', 'Hold length must be positive');
  if (toInstant(span(r).start) <= ctx.now) fail('invalid', 'Cannot hold dates in the past');
  requireAvailable(world, r, ctx);
  const n = clone(r);
  if (n.hold) n.holdHistory.push(n.hold);
  const placed = nowLocal(ctx);
  n.hold = { placed, expires: addHours(placed, hours), hours };
  n.status = 'hold';
  log(n, ctx, `Dates held for ${hours}h, until ${n.hold.expires.replace('T', ' ')}`);
  return { value: n, warnings: [] };
}

export function extendHold(r: BookingRecord, hours: number, ctx: Ctx): Result<BookingRecord> {
  requirePhase(r, ctx, ['hold'], 'extend');
  const n = clone(r);
  n.holdHistory.push({ ...n.hold! });
  n.hold!.expires = addHours(n.hold!.expires, hours);
  n.hold!.hours += hours;
  log(n, ctx, `Hold extended by ${hours}h, until ${n.hold!.expires.replace('T', ' ')}`);
  return { value: n, warnings: [] };
}

/** Release ends the hold early; the record becomes Lost (it can be reopened). */
export function releaseHold(r: BookingRecord, reason: string, ctx: Ctx): Result<BookingRecord> {
  requirePhase(r, ctx, ['hold'], 'release');
  const n = clone(r);
  n.holdHistory.push({ ...n.hold!, expires: nowLocal(ctx) });
  n.status = 'lost';
  n.lost = { date: today(ctx), reason: reason || 'Hold released' };
  log(n, ctx, `Hold released — ${n.lost.reason}`);
  return { value: n, warnings: [] };
}

/* ---------------- confirmation & pricing ---------------- */

export interface PricingPatch {
  agreed?: Rupees[];
  extra?: BookingRecord['extra'];
  cleaning?: Rupees;
  discount?: BookingRecord['discount'];
}

function applyPricing(n: BookingRecord, p: PricingPatch) {
  if (p.agreed) {
    if (p.agreed.length !== n.items.length) fail('invalid', 'One agreed price per item');
    p.agreed.forEach((a, i) => { assertNonNegative(a, 'agreed price'); n.items[i]!.agreed = a; });
  }
  if ('extra' in p) { if (p.extra) assertNonNegative(p.extra.amount, 'extra-guest charge'); n.extra = p.extra ?? null; }
  if (p.cleaning !== undefined) { assertNonNegative(p.cleaning, 'cleaning'); n.cleaning = p.cleaning; }
  if ('discount' in p) { if (p.discount) assertNonNegative(p.discount.amount, 'discount'); n.discount = p.discount ?? null; }
}

export function adjustPricing(r: BookingRecord, p: PricingPatch, ctx: Ctx): Result<BookingRecord> {
  requirePhase(r, ctx, ['enquiry', 'hold', 'expired', 'upcoming', 'inprogress', 'closing'], 'adjust pricing on');
  const before = calc(r).total;
  const n = clone(r);
  applyPricing(n, p);
  const k = calc(n);
  if (k.total <= 0 && !k.pending) fail('invalid', 'Total must be more than ₹0');
  if (k.paid > k.total) fail('invalid', `Total cannot be below the ${inr(k.paid)} already received`);
  if (k.total !== before) log(n, ctx, `Pricing adjusted — total ${inr(before)} → ${inr(k.total)}`);
  return { value: n, warnings: [] };
}

function newPayment(ctx: Ctx, p: { id?: string; date?: LocalDate; type: PaymentType; mode: string; amount: Rupees; ref?: string }): Payment {
  assertNonNegative(p.amount, 'payment');
  if (p.amount <= 0) fail('invalid', 'Enter an amount');
  if (!p.mode) fail('invalid', 'Choose a payment mode');
  return { id: p.id ?? newId(ctx.now), date: p.date ?? today(ctx), type: p.type, mode: p.mode, amount: p.amount, ref: p.ref ?? '', recordedAt: nowLocal(ctx), by: ctx.actor, voided: null };
}

export function confirm(r: BookingRecord, o: { pricing?: PricingPatch; advance: { id?: string; amount: Rupees; mode: string; ref?: string; date?: LocalDate } | null }, world: World, ctx: Ctx): Result<BookingRecord> {
  requirePhase(r, ctx, ['enquiry', 'hold', 'expired'], 'confirm');
  if (toInstant(span(r).start) <= ctx.now) fail('invalid', 'Cannot confirm dates in the past');
  requireAvailable(world, r, ctx);
  const n = clone(r);
  if (o.pricing) applyPricing(n, o.pricing);
  requirePrice(n, 'confirming');
  const warnings: string[] = [];
  n.status = 'confirmed';
  if (n.hold) { n.holdHistory.push(n.hold); n.hold = null; }
  if (o.advance && o.advance.amount > 0) {
    if (n.payments.some(p => p.id === o.advance!.id)) fail('invalid', 'This payment was already recorded');
    n.payments.push(newPayment(ctx, { ...o.advance, type: 'advance' }));
  }
  const k = calc(n);
  if (k.paid > k.total) fail('over-balance', 'Advance is more than the booking total');
  if (k.paid < k.advanceDue) warnings.push(`Advance received ${inr(k.paid)} is below the ${inr(k.advanceDue)} usually required`);
  log(n, ctx, `Booking confirmed${o.advance?.amount ? ` with advance ${inr(o.advance.amount)} (${o.advance.mode})` : ''}`);
  return { value: n, warnings };
}

/* ---------------- money ---------------- */

/**
 * Idempotent: if a payment with the same id already exists the record is returned unchanged, so a
 * retried save can never record the same payment twice.
 */
export function recordPayment(r: BookingRecord, p: { id: string; date?: LocalDate; type: Exclude<PaymentType, 'refund'>; mode: string; amount: Rupees; ref?: string }, ctx: Ctx): Result<BookingRecord> {
  if (r.payments.some(x => x.id === p.id)) return { value: r, warnings: ['Already recorded'] };
  requirePhase(r, ctx, ['upcoming', 'inprogress', 'closing'], 'record a payment on');
  const k = calc(r);
  if (p.amount > k.balance) fail('over-balance', `Amount is more than the balance of ${inr(k.balance)}`);
  const n = clone(r);
  const pay = newPayment(ctx, p);
  n.payments.push(pay);
  n.payments.sort((a, b) => a.date.localeCompare(b.date) || a.recordedAt.localeCompare(b.recordedAt));
  log(n, ctx, `${p.type === 'advance' ? 'Booking advance' : p.type === 'final' ? 'Final payment' : 'Part payment'} received — ${inr(p.amount)} (${p.mode})`);
  return { value: n, warnings: [] };
}

/**
 * Used only when replaying another device's payment that no longer fits the balance (two people
 * recorded money at the same time). Money that was really received is never dropped: the payment is
 * kept and the caller flags the record for review.
 */
export function appendPaymentForReview(r: BookingRecord, p: { id: string; date?: LocalDate; type: PaymentType; mode: string; amount: Rupees; ref?: string }, ctx: Ctx): BookingRecord {
  if (r.payments.some(x => x.id === p.id)) return r;
  const n = clone(r);
  n.payments.push(newPayment(ctx, p));
  log(n, ctx, `${inr(p.amount)} (${p.mode}) recorded on another device at the same time — check for a duplicate`);
  return n;
}

export function voidPayment(r: BookingRecord, paymentId: string, reason: string, ctx: Ctx): Result<BookingRecord> {
  if (!reason.trim()) fail('invalid', 'Give a reason');
  const n = clone(r);
  const p = n.payments.find(x => x.id === paymentId) ?? fail('not-found', 'Payment not found');
  if (p.voided) return { value: r, warnings: ['Already voided'] };
  if (n.status === 'cancelled' || n.status === 'completed') fail('wrong-state', 'Payments on closed records cannot be voided');
  p.voided = { at: nowLocal(ctx), by: ctx.actor, reason };
  log(n, ctx, `Payment of ${inr(p.amount)} on ${p.date} voided — ${reason}`);
  return { value: n, warnings: [] };
}

export function collectDeposit(r: BookingRecord, o: { amount: Rupees; mode: string; date?: LocalDate }, ctx: Ctx): Result<BookingRecord> {
  requirePhase(r, ctx, ['upcoming', 'inprogress', 'closing'], 'collect a deposit on');
  if (r.deposit.status !== 'due') fail('wrong-state', 'Deposit already collected');
  assertNonNegative(o.amount, 'deposit');
  if (o.amount <= 0) fail('invalid', 'Enter the deposit amount');
  const n = clone(r);
  n.deposit.collected = { date: o.date ?? today(ctx), mode: o.mode, amount: o.amount, by: ctx.actor };
  n.deposit.status = 'held';
  log(n, ctx, `Caution deposit collected — ${inr(o.amount)} (${o.mode})`);
  return { value: n, warnings: o.amount !== r.deposit.required ? [`Required deposit is ${inr(r.deposit.required)}`] : [] };
}

/* ---------------- check-out & closure ---------------- */

/** Step 1: guests have left. A held deposit moves to inspection. */
export function startCheckout(r: BookingRecord, ctx: Ctx): Result<BookingRecord> {
  requirePhase(r, ctx, ['inprogress', 'closing'], 'check out');
  if (r.checkout) return { value: r, warnings: ['Already checked out'] };
  const n = clone(r);
  n.checkout = { at: nowLocal(ctx), by: ctx.actor };
  if (n.deposit.status === 'held') n.deposit.status = 'inspection';
  log(n, ctx, 'Guests checked out');
  return { value: n, warnings: [] };
}

export interface Settlement { deducted: Rupees; refund: Rupees; excess: Rupees }

export function settlementFor(collected: Rupees, deductions: readonly { amount: Rupees }[]): Settlement {
  const deducted = sum(deductions.map(d => d.amount));
  return { deducted, refund: Math.max(0, collected - deducted), excess: Math.max(0, deducted - collected) };
}

/**
 * Step 3–4: inspection result. Deductions come out of the deposit; anything beyond the deposit is
 * added to the booking as "Damage beyond deposit" (which then shows as balance due). The deposit is
 * never booking revenue; only that excess is.
 */
export function settleDeposit(r: BookingRecord, o: { deductions: (Omit<Deduction, 'id'> & { id?: string })[]; mode: string; ref?: string; notes?: string; evidence?: string[] }, ctx: Ctx): Result<BookingRecord> {
  requirePhase(r, ctx, ['inprogress', 'closing'], 'settle the deposit on');
  if (!r.checkout) fail('wrong-state', 'Check the guests out first');
  if (!['held', 'inspection'].includes(r.deposit.status)) fail('wrong-state', 'No deposit is waiting for inspection');
  o.deductions.forEach(d => { assertNonNegative(d.amount, 'deduction'); if (!d.reason.trim()) fail('invalid', 'Each deduction needs a reason'); });
  const n = clone(r), d = n.deposit, collected = d.collected!.amount;
  d.deductions = o.deductions.map(x => ({ ...x, id: x.id ?? newId(ctx.now) }));
  d.notes = o.notes ?? '';
  d.evidence = o.evidence ?? [];
  const s = settlementFor(collected, d.deductions);
  if (s.refund > 0) d.refund = { date: today(ctx), mode: o.mode, amount: s.refund, ref: o.ref ?? '', by: ctx.actor };
  d.status = s.refund === 0 ? 'forfeited' : s.deducted > 0 ? 'partial' : 'refunded';
  if (s.deducted) log(n, ctx, `Inspection: ${d.deductions.map(x => `${inr(x.amount)} deducted — ${x.reason}`).join('; ')}`);
  else log(n, ctx, 'Inspection: no damage');
  if (s.refund > 0) log(n, ctx, `Deposit refund — ${inr(s.refund)} (${o.mode})`);
  if (s.excess > 0) { n.damage += s.excess; log(n, ctx, `Damage beyond deposit billed — ${inr(s.excess)}`); }
  return { value: n, warnings: [] };
}

/** Completes only when nothing is owed and the deposit is settled; otherwise it stays Awaiting closure. */
export function complete(r: BookingRecord, ctx: Ctx): Result<BookingRecord> {
  requirePhase(r, ctx, ['inprogress', 'closing'], 'complete');
  if (!r.checkout) fail('wrong-state', 'Check the guests out first');
  const k = calc(r);
  if (k.balance > 0) fail('wrong-state', `Balance of ${inr(k.balance)} is still due`);
  if (k.balance < 0) fail('wrong-state', `Received ${inr(-k.balance)} more than the total — record a refund first`);
  if (['held', 'inspection'].includes(r.deposit.status)) fail('wrong-state', 'Settle the caution deposit first');
  const n = clone(r);
  n.status = 'completed';
  n.completedAt = nowLocal(ctx);
  log(n, ctx, 'Booking completed');
  return { value: n, warnings: [] };
}

/** Refund an overpayment on an open booking (e.g. after a price reduction). */
export function recordRefund(r: BookingRecord, p: { id: string; amount: Rupees; mode: string; ref?: string; date?: LocalDate }, ctx: Ctx): Result<BookingRecord> {
  if (r.payments.some(x => x.id === p.id)) return { value: r, warnings: ['Already recorded'] };
  requirePhase(r, ctx, ['upcoming', 'inprogress', 'closing'], 'refund');
  const k = calc(r);
  if (p.amount > Math.max(0, -k.balance)) fail('over-balance', 'Refund is more than the overpayment');
  const n = clone(r);
  n.payments.push(newPayment(ctx, { ...p, type: 'refund' }));
  log(n, ctx, `Refund paid — ${inr(p.amount)} (${p.mode})`);
  return { value: n, warnings: [] };
}

/* ---------------- side exits ---------------- */

export function cancel(r: BookingRecord, o: { reason: string; refund: Rupees; mode: string; refundId?: string; ref?: string; notes?: string }, ctx: Ctx): Result<BookingRecord> {
  requirePhase(r, ctx, ['upcoming'], 'cancel');
  if (!o.reason.trim()) fail('invalid', 'Give a reason');
  assertNonNegative(o.refund, 'refund');
  const k = calc(r);
  if (o.refund > k.paid) fail('over-balance', `Refund cannot be more than the ${inr(k.paid)} paid`);
  const n = clone(r);
  n.status = 'cancelled';
  n.cancel = { date: today(ctx), reason: o.reason, paid: k.paid, refund: o.refund, retained: k.paid - o.refund, notes: o.notes ?? '' };
  if (o.refund > 0) n.payments.push(newPayment(ctx, { id: o.refundId, type: 'refund', mode: o.mode, amount: o.refund, ref: o.ref }));
  log(n, ctx, `Booking cancelled — ${o.reason}. Refund ${inr(o.refund)}, retained ${inr(k.paid - o.refund)}. Dates released.`);
  // A caution deposit taken before arrival is returned in full: there was no stay to inspect.
  if (n.deposit.collected && n.deposit.status === 'held') {
    n.deposit.refund = { date: today(ctx), mode: o.mode, amount: n.deposit.collected.amount, ref: '', by: ctx.actor };
    n.deposit.status = 'refunded';
    log(n, ctx, `Caution deposit returned in full — ${inr(n.deposit.collected.amount)} (${o.mode})`);
  }
  return { value: n, warnings: [] };
}

export const LOST_REASONS = ['Date unavailable', 'Budget', 'Chose another venue', 'No response', 'Plans changed', 'Other'];

export function markLost(r: BookingRecord, reason: string, ctx: Ctx): Result<BookingRecord> {
  requirePhase(r, ctx, ['enquiry', 'hold', 'expired'], 'mark lost');
  if (!reason.trim()) fail('invalid', 'Give a reason');
  const n = clone(r);
  if (n.hold && n.status === 'hold') n.holdHistory.push({ ...n.hold, expires: nowLocal(ctx) });
  n.status = 'lost';
  n.lost = { date: today(ctx), reason };
  log(n, ctx, `Marked lost — ${reason}`);
  return { value: n, warnings: [] };
}

export function reopen(r: BookingRecord, ctx: Ctx): Result<BookingRecord> {
  requirePhase(r, ctx, ['lost'], 'reopen');
  const n = clone(r);
  n.status = 'enquiry';
  n.lost = null;
  n.hold = null;
  log(n, ctx, 'Reopened as enquiry');
  return { value: n, warnings: [] };
}

/* ---------------- notes, documents, messages ---------------- */

export function setNotes(r: BookingRecord, notes: string, ctx: Ctx): Result<BookingRecord> {
  const n = clone(r); n.notes = notes; log(n, ctx, 'Notes updated'); return { value: n, warnings: [] };
}

export function setFollowUp(r: BookingRecord, date: LocalDate | null, ctx: Ctx): Result<BookingRecord> {
  const n = clone(r); n.followUp = date; log(n, ctx, date ? `Follow-up set for ${date}` : 'Follow-up cleared'); return { value: n, warnings: [] };
}

export function setGuests(r: BookingRecord, guests: number, ctx: Ctx): Result<BookingRecord> {
  if (!Number.isInteger(guests) || guests <= 0) fail('invalid', 'Enter the number of guests');
  const n = clone(r); log(n, ctx, `Guests ${r.guests} → ${guests}`); n.guests = guests; return { value: n, warnings: [] };
}

export interface DetailsPatch {
  /** One per existing item, same order and kind: package, start and end. */
  items?: ItemInput[];
  guests?: number;
  type?: string;
}

/**
 * Change the date, times, package, guests or occasion of an enquiry, hold or upcoming booking.
 * Prices follow the change only where the owner had kept the standard rate (and the standard cleaning);
 * a price agreed by hand is kept, so the owner adjusts it if needed. Dates that block the calendar are
 * checked for clashes, excluding this record itself.
 */
export function editDetails(r: BookingRecord, p: DetailsPatch, world: World, ctx: Ctx): Result<BookingRecord> {
  const ph = requirePhase(r, ctx, ['enquiry', 'hold', 'expired', 'upcoming'], 'edit');
  const n = clone(r);
  const changes: string[] = [];
  if (p.items) {
    if (p.items.length !== r.items.length) fail('invalid', 'One date per package');
    n.items = p.items.map((x, i) => {
      const old = r.items[i]!;
      const pkg = ctx.settings.packages.find(q => q.id === x.pkg) ?? fail('invalid', `Unknown package ${x.pkg}`);
      if (pkg.kind !== old.kind) fail('invalid', `Choose ${old.kind === 'event' ? 'an event' : 'a stay'} package`);
      if (!isValidLocal(x.start) || !isValidLocal(x.end)) fail('invalid', 'Enter valid dates and times');
      if (x.pkg === old.pkg && x.start === old.start && x.end === old.end) return old;
      const keep = old.custom || old.agreed !== old.std ? old.agreed : undefined;
      // Same package: keep a hand-agreed price. New package: its standard rate (a Customize package starts
      // from the price agreed so far, so a booked event never ends up with no price).
      const it = quoteItem(pkg, x.start, x.end, x.pkg === old.pkg ? keep : pkg.custom ? old.agreed : undefined);
      if (x.pkg !== old.pkg) changes.push(`Package ${old.pkgName} → ${it.pkgName}`);
      if (x.start !== old.start || x.end !== old.end) changes.push(`${old.pkgName === it.pkgName ? '' : it.pkgName + ' '}dates ${old.start.replace('T', ' ')} – ${old.end.replace('T', ' ')} → ${it.start.replace('T', ' ')} – ${it.end.replace('T', ' ')}`.trim());
      return it;
    });
    if (r.cleaning === cleaningFor(r.items, ctx.settings)) n.cleaning = cleaningFor(n.items, ctx.settings);
    if (!n.deposit.collected) n.deposit.required = depositFor(n.items);
  }
  if (p.guests !== undefined && p.guests !== r.guests) {
    if (!Number.isInteger(p.guests) || p.guests <= 0) fail('invalid', 'Enter the number of guests');
    changes.push(`Guests ${r.guests} → ${p.guests}`);
    n.guests = p.guests;
  }
  if (p.type !== undefined && p.type.trim() && p.type.trim() !== r.type) {
    changes.push(`Occasion ${r.type} → ${p.type.trim()}`);
    n.type = p.type.trim();
  }
  if (!changes.length) return { value: r, warnings: [] };
  if (p.items && ph !== 'enquiry' && ph !== 'expired') {
    if (toInstant(span(n).start) <= ctx.now) fail('invalid', 'The new date is in the past');
    requireAvailable(world, n, ctx);
  }
  const k = calc(n);
  if (k.paid > k.total && !k.pending) fail('invalid', `The new total ${inr(k.total)} is below the ${inr(k.paid)} already received — adjust the pricing first`);
  const warnings: string[] = [];
  for (const it of n.items) {
    const pkg = ctx.settings.packages.find(q => q.id === it.pkg);
    if (pkg && !pkg.custom && n.guests > (pkg.max ?? pkg.capacity)) warnings.push(`${n.guests} guests is above the maximum of ${pkg.max ?? pkg.capacity} for ${pkg.name}`);
  }
  log(n, ctx, `Details changed — ${changes.join('; ')}`);
  return { value: n, warnings };
}

export function addDoc(r: BookingRecord, d: DocRef, ctx: Ctx): Result<BookingRecord> {
  if (r.docs.some(x => x.id === d.id)) return { value: r, warnings: ['Already added'] };
  const n = clone(r); n.docs.push(d); log(n, ctx, `Document added — ${d.name} (${d.cat})`); return { value: n, warnings: [] };
}

export function logComm(r: BookingRecord, c: { id: string; key: string; action: Comm['action'] }, ctx: Ctx): Result<BookingRecord> {
  if (r.comms.some(x => x.id === c.id)) return { value: r, warnings: [] };
  const n = clone(r);
  n.comms.push({ id: c.id, key: c.key, action: c.action, date: nowLocal(ctx), by: ctx.actor });
  return { value: n, warnings: [] };
}

/* ---------------- owner blocks ---------------- */

export function makeBlock(b: { id?: string; type: string; start: string; end: string; note?: string }, world: World, ctx: Ctx): { value: Block; conflicts: Conflict[] } {
  if (!isValidLocal(b.start) || !isValidLocal(b.end) || toInstant(b.end) <= toInstant(b.start)) fail('invalid', 'Enter a valid period');
  if (!b.type) fail('invalid', 'Choose a block type');
  const cs = conflicts({ records: world.records, blocks: [] }, b.start, b.end, { now: ctx.now, bufferHours: 0 });
  if (cs.length) throw new DomainError('unavailable', 'Blocks cannot overlap holds or bookings', cs);
  return { value: { id: b.id ?? newId(ctx.now), type: b.type, start: b.start, end: b.end, note: b.note ?? '' }, conflicts: [] };
}

/* ---------------- customers ---------------- */

export function makeCustomer(c: { id?: string; name: string; phone: string; whatsapp?: string; alt?: string; email?: string; notes?: string }, ctx: Ctx): Customer {
  if (!c.name.trim()) fail('invalid', 'Enter the customer name');
  const digits = c.phone.replace(/\D/g, '').replace(/^91(?=\d{10}$)/, '');
  if (!/^\d{10}$/.test(digits)) fail('invalid', 'Enter a 10-digit mobile number');
  return { id: c.id ?? newId(ctx.now), name: c.name.trim(), phone: c.phone.trim(), whatsapp: (c.whatsapp || c.phone).trim(), alt: c.alt ?? '', email: c.email ?? '', notes: c.notes ?? '', since: today(ctx), docs: [] };
}

